import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { patchWindowStyles } from './colorSanitizer';

async function convertImgToBase64(src: string): Promise<string> {
  if (!src || src.startsWith('data:')) return src;
  try {
    const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(src)}&format=json`;
    const res = await fetch(proxyUrl);
    if (res.ok) {
      const json = await res.json();
      if (json.success && json.base64) return json.base64;
    }
  } catch {}
  return src;
}

export async function exportTechnicalSheetToPdf(
  elementId: string,
  fileName: string
): Promise<boolean> {
  const originalElement = document.getElementById(elementId);
  if (!originalElement) {
    throw new Error('No se encontró el contenedor de la ficha técnica.');
  }

  // Pre-cargar imágenes del elemento original a Base64 antes de clonar
  const originalImages = Array.from(originalElement.querySelectorAll('img'));
  await Promise.all(
    originalImages.map(async (img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('data:')) {
        const b64 = await convertImgToBase64(src);
        if (b64 && b64.startsWith('data:')) {
          img.setAttribute('src', b64);
        }
      }
    })
  );

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  // 1. Crear un contenedor aislado off-screen en document.body para desacoplarlo
  // de cualquier transform: scale(...) del modal en celulares y de los viewports móviles.
  const offscreenContainer = document.createElement('div');
  offscreenContainer.id = 'pdf-render-sandbox';
  offscreenContainer.style.position = 'fixed';
  offscreenContainer.style.left = '-99999px';
  offscreenContainer.style.top = '0';
  offscreenContainer.style.width = '800px';
  offscreenContainer.style.minWidth = '800px';
  offscreenContainer.style.maxWidth = '800px';
  offscreenContainer.style.zIndex = '-9999';
  offscreenContainer.style.backgroundColor = '#ffffff';
  offscreenContainer.style.transform = 'none';
  offscreenContainer.style.opacity = '1';
  offscreenContainer.style.pointerEvents = 'none';
  offscreenContainer.style.webkitTextSizeAdjust = '100%';
  (offscreenContainer.style as any).textSizeAdjust = '100%';

  // Clonar el elemento de la ficha técnica
  const clonedForRender = originalElement.cloneNode(true) as HTMLElement;
  clonedForRender.id = 'pdf-render-element';
  clonedForRender.style.width = '800px';
  clonedForRender.style.minWidth = '800px';
  clonedForRender.style.maxWidth = '800px';
  clonedForRender.style.margin = '0 auto';
  clonedForRender.style.transform = 'none';
  clonedForRender.style.webkitTextSizeAdjust = '100%';
  (clonedForRender.style as any).textSizeAdjust = '100%';

  offscreenContainer.appendChild(clonedForRender);
  document.body.appendChild(offscreenContainer);

  const unpatchMain = patchWindowStyles(window);

  try {
    // Dar un microtick para asegurar que el clon esté completamente computado en el layout
    await new Promise((resolve) => setTimeout(resolve, 60));

    // scale: 2 ofrece máxima nitidez vectorial en impresión A4 y evita artefactos
    const canvas = await html2canvas(clonedForRender, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      width: 800,
      windowWidth: 1200,
      windowHeight: 1600,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      onclone: (clonedDoc, clonedElement) => {
        if (clonedDoc.defaultView) {
          patchWindowStyles(clonedDoc.defaultView);
        }
        const style = clonedDoc.createElement('style');
        style.textContent = `
          html {
            font-size: 13.5px !important;
            -webkit-text-size-adjust: 100% !important;
            -moz-text-size-adjust: 100% !important;
            text-size-adjust: 100% !important;
          }
          body, #pdf-render-sandbox, #pdf-render-element, #pdf-render-element * {
            -webkit-text-size-adjust: 100% !important;
            -moz-text-size-adjust: 100% !important;
            text-size-adjust: 100% !important;
            -webkit-font-smoothing: antialiased !important;
            text-rendering: geometricPrecision !important;
            font-variant-ligatures: none !important;
            transform: none !important;
          }
          img {
            display: inline-block !important;
            vertical-align: baseline !important;
          }
          *, *::before, *::after {
            letter-spacing: normal !important;
            word-spacing: normal !important;
            box-sizing: border-box !important;
          }
          body, p, span, h1, h2, h3, h4, div, a, td, th {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
          }
        `;
        clonedDoc.head.appendChild(style);

        if (clonedDoc.body) {
          clonedDoc.body.style.backgroundColor = '#ffffff';
          clonedDoc.body.style.color = '#1e293b';
          clonedDoc.body.style.webkitTextSizeAdjust = '100%';
          (clonedDoc.body.style as any).textSizeAdjust = '100%';
        }
        clonedElement.style.width = '800px';
        clonedElement.style.minWidth = '800px';
        clonedElement.style.maxWidth = '800px';
        clonedElement.style.margin = '0 auto';
        clonedElement.style.boxSizing = 'border-box';
        clonedElement.style.transform = 'none';
      },
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const marginMm = 8;
    const printableWidth = pageWidth - marginMm * 2;
    const printableHeight = pageHeight - marginMm * 2;

    let finalWidth = printableWidth;
    let finalHeight = (canvas.height * finalWidth) / canvas.width;

    if (finalHeight > printableHeight) {
      finalHeight = printableHeight;
      finalWidth = (canvas.width * finalHeight) / canvas.height;
    }

    const xOffset = marginMm + (printableWidth - finalWidth) / 2;
    const yOffset = marginMm + (printableHeight - finalHeight) / 2;

    pdf.addImage(imgData, 'JPEG', xOffset, yOffset, finalWidth, finalHeight);
    pdf.save(fileName);
    return true;
  } finally {
    unpatchMain();
    if (offscreenContainer.parentNode) {
      offscreenContainer.parentNode.removeChild(offscreenContainer);
    }
  }
}
